import { useState, useEffect } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

/**
 * Convert a base64 URL-safe string to a Uint8Array
 * Required for VAPID key conversion
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if push notifications are supported
    const supported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;

    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();

      // Re-check whenever the app becomes visible again — iOS silently invalidates
      // push subscriptions during inactivity, so we need to detect and recover.
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          checkSubscription();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
  }, []);

  /**
   * Check if user has an active push subscription.
   * If permission is already granted but the subscription is missing (iOS silently
   * invalidated it), attempt a silent re-subscribe without prompting the user.
   */
  async function checkSubscription() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription !== null) {
        setIsSubscribed(true);
      } else if (Notification.permission === 'granted') {
        // Subscription was silently dropped — attempt background recovery.
        // We don't call subscribe() here because that calls requestPermission(),
        // which requires a user gesture on iOS and will fail silently or return
        // 'denied', turning the bell red. Instead we go straight to subscription
        // creation and swallow failures quietly so the user can click to retry.
        await silentResubscribe(registration);
      } else {
        setIsSubscribed(false);
      }
    } catch (err) {
      console.error('Error checking subscription:', err);
    }
  }

  /**
   * Attempt to recreate a push subscription without requesting permission
   * (permission is already granted). On failure, marks as unsubscribed so
   * the user can click to subscribe manually — does not set error state.
   */
  async function silentResubscribe(registration: ServiceWorkerRegistration) {
    try {
      const response = await fetch(`${API_URL}/push/vapid-public-key`);
      if (!response.ok) throw new Error('Failed to get VAPID public key');
      const { publicKey } = await response.json();

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });

      const saveResponse = await fetch(`${API_URL}/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription.toJSON()),
      });

      if (!saveResponse.ok) throw new Error('Failed to save subscription on server');

      setIsSubscribed(true);
    } catch (err) {
      // Silent failure — don't set error state, just mark as unsubscribed so
      // the bell shows as inactive rather than red.
      console.error('Silent resubscribe failed:', err);
      setIsSubscribed(false);
    }
  }

  /**
   * Subscribe to push notifications
   */
  async function subscribe() {
    setIsSubscribing(true);
    setError(null);

    try {
      // Request notification permission
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') {
        throw new Error('Notification permission denied');
      }

      // Get VAPID public key from server
      const response = await fetch(`${API_URL}/push/vapid-public-key`);
      if (!response.ok) {
        throw new Error('Failed to get VAPID public key');
      }
      const { publicKey } = await response.json();

      // Subscribe to push notifications
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });

      // Send subscription to server
      const subscribeResponse = await fetch(`${API_URL}/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subscription.toJSON()),
      });

      if (!subscribeResponse.ok) {
        throw new Error('Failed to save subscription on server');
      }

      setIsSubscribed(true);
    } catch (error: any) {
      console.error('Error subscribing to push notifications:', error);
      setError(error.message);
    } finally {
      setIsSubscribing(false);
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async function unsubscribe() {
    setIsUnsubscribing(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        setIsSubscribed(false);
        return;
      }

      // Unsubscribe on client
      await subscription.unsubscribe();

      // Remove subscription from server
      await fetch(`${API_URL}/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
        }),
      });

      setIsSubscribed(false);
    } catch (error: any) {
      console.error('Error unsubscribing from push notifications:', error);
      setError(error.message);
    } finally {
      setIsUnsubscribing(false);
    }
  }

  return {
    isSupported,
    isSubscribed,
    isSubscribing,
    isUnsubscribing,
    permission,
    error,
    subscribe,
    unsubscribe,
  };
}
